/**
 * CustomerFiles (FILES-01a, CRM-003 slice 1) — the "Files & documents" SOON
 * card made real. Photos of equipment and job sites, permits, signed
 * proposals — attached at the customer level, scoped to the viewed property.
 *
 * How the field leaders do it: ServiceTitan keeps "Photos & Videos" +
 * "Attachments" sections on every record (tech uploads ride their mobile
 * app); Jobber attaches floor plans/permits on the client profile. Adopted:
 * record-level files, photo-vs-document split, captions. Adapted: v1 is
 * office-side upload from the 360 — tech photo upload lands with the mobile
 * app. Rejected for v1: separate video handling.
 *
 * Data/upload/archive primitives live in features/files/filesCore (shared
 * with DispatchFiles — one bucket, one table, every surface).
 */
import { useRef, useState } from "react";
import { FolderOpen, FileText, ImagePlus, Loader2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { Card } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import {
  useCustomerFiles,
  useSignedUrls,
  useUploadFiles,
  prettySize,
  fileOrigin,
  type PegasusFile,
} from "@/features/files/filesCore";
import { ArchiveFileButton } from "@/features/files/ArchiveFileButton";
import { FilePreviewModal } from "@/features/files/FilePreviewModal";

export function CustomerFiles({
  custno,
  locno,
}: {
  custno: string;
  locno?: string | null;
}) {
  const { data, isLoading } = useCustomerFiles(custno);
  const all = data ?? [];
  // Property-scoped: files for the viewed location + customer-level files
  // (no locno). Files on OTHER properties surface as a count, never hidden.
  const files = locno ? all.filter((f) => !f.locno || f.locno === locno) : all;
  const otherCount = all.length - files.length;
  const urls = useSignedUrls(files).data;
  const upload = useUploadFiles({ custno, locno });
  const inputRef = useRef<HTMLInputElement>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const photos = files.filter((f) => f.kind === "photo");
  const docs = files.filter((f) => f.kind === "document");
  const photosShown = showAll ? photos : photos.slice(0, 6);
  const docsShown = showAll ? docs : docs.slice(0, 4);
  const hidden = photos.length - photosShown.length + (docs.length - docsShown.length);

  function pick() {
    inputRef.current?.click();
  }

  function onPicked(list: FileList | null) {
    if (!list || list.length === 0) return;
    setNotice(null);
    upload.mutate([...list], {
      onSuccess: (r) =>
        setNotice(r.skipped.length ? `Skipped: ${r.skipped.join(", ")}` : null),
      onError: (e) => setNotice(`Upload failed — ${(e as Error).message}`),
    });
    if (inputRef.current) inputRef.current.value = "";
  }

  // FILES-04 (Seth): files open in the preview modal — view, download,
  // open-in-tab, email it, text it. No more bare new-tab bounce.
  const [preview, setPreview] = useState<PegasusFile | null>(null);
  function openFile(f: PegasusFile) {
    setPreview(f);
  }

  return (
    <>
    <FilePreviewModal file={preview} custno={custno} onClose={() => setPreview(null)} />
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-100 bg-gradient-to-r from-slate-50/90 via-transparent to-transparent px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
            <FolderOpen className="h-3.5 w-3.5" />
          </span>
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
            Files &amp; photos
          </span>
        </div>
        <button
          type="button"
          onClick={pick}
          disabled={upload.isPending}
          title="Add photos or documents to this customer's record"
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-brand-700 transition-colors hover:bg-brand-50 hover:text-brand-900 disabled:text-slate-400"
        >
          {upload.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <ImagePlus className="h-3 w-3" />
          )}
          {upload.isPending ? "Uploading…" : "Add files"}
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => onPicked(e.target.files)}
        />
      </div>

      <div className="px-4 py-3">
        {isLoading ? (
          <div className="h-14 animate-pulse rounded-lg bg-slate-100" />
        ) : files.length === 0 ? (
          <p className="text-xs leading-relaxed text-slate-400">
            {otherCount > 0
              ? `No files for this property yet — ${otherCount} on this customer's other properties (switch properties above to see them).`
              : "No files yet. Equipment photos, permits, and signed proposals all land here — tech photo uploads ride the mobile app later."}
          </p>
        ) : (
          <div className="space-y-2.5">
            {photosShown.length > 0 && (
              <div className="grid grid-cols-3 gap-1.5">
                {photosShown.map((f) => (
                  <div key={f.id} className="group relative">
                    <button
                      type="button"
                      onClick={() => openFile(f)}
                      title={`${f.filename} · ${format(parseISO(f.created_at), "MMM d, yyyy")} — opens the photo`}
                      className="relative block h-16 w-full overflow-hidden rounded-lg bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
                    >
                      {urls?.get(f.storage_path) ? (
                        <img
                          src={urls.get(f.storage_path)}
                          alt={f.caption ?? f.filename}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                        />
                      ) : (
                        <span className="flex h-full items-center justify-center text-slate-300">
                          <ImagePlus className="h-4 w-4" />
                        </span>
                      )}
                    </button>
                    {/* 360-AUDIT wave 2 (A): visible at rest + two-click confirm. */}
                    <ArchiveFileButton fileId={f.id} filename={f.filename} look="overlay" className="h-4 w-4" />
                  </div>
                ))}
              </div>
            )}
            {docsShown.length > 0 && (
              <ul className="space-y-1">
                {docsShown.map((f) => (
                  <li key={f.id} className="group flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => openFile(f)}
                      title="Opens the document"
                      className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-slate-50"
                    >
                      <FileText className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                      <span className="min-w-0 flex-1 truncate text-xs font-medium text-slate-700">
                        {f.filename}
                      </span>
                      {f.source === "esc" && (
                        <span
                          className="shrink-0 rounded bg-slate-100 px-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400"
                          title={`Brought over from ESC · added ${format(parseISO(f.created_at), "MMM d, yyyy")} by ${f.uploaded_by ?? "?"}`}
                        >
                          esc
                        </span>
                      )}
                      {/* FILES-CALL-01: who it came from, at a glance. */}
                      {fileOrigin(f) === "customer" && (
                        <span
                          className="shrink-0 rounded bg-violet-50 px-1 text-[10px] font-semibold uppercase tracking-wide text-violet-700"
                          title="Sent by the customer through the portal"
                        >
                          customer
                        </span>
                      )}
                      {fileOrigin(f) === "tech" && (
                        <span
                          className="shrink-0 rounded bg-slate-100 px-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500"
                          title="Uploaded from the phone on a call"
                        >
                          phone
                        </span>
                      )}
                      <span className="shrink-0 text-[11px] tabular-nums text-slate-400">
                        {prettySize(f.size_bytes)}
                      </span>
                    </button>
                    <ArchiveFileButton fileId={f.id} filename={f.filename} look="row" className="h-5 w-5" />
                  </li>
                ))}
              </ul>
            )}
            {hidden > 0 && !showAll && (
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="text-xs font-medium text-brand-600 transition-colors hover:text-brand-700"
              >
                Show all {files.length} files
              </button>
            )}
            {otherCount > 0 && (
              <p className="border-t border-slate-100 pt-2 text-[11px] text-slate-400">
                {otherCount} more on this customer&apos;s other properties.
              </p>
            )}
          </div>
        )}
        {notice && (
          <p className={cn("mt-2 text-[11px]", notice.startsWith("Upload failed") ? "text-red-600" : "text-amber-700")}>
            {notice}
          </p>
        )}
      </div>
    </Card>
    </>
  );
}
